import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from "@mui/material";
import React, { createContext, useContext, useState } from "react";
import { useTranslation } from "react-i18next";

type OpenDialog = (
	title: string,
	content: string,
	confirmCb: () => Promise<void> | void,
	closeCallback?: () => void
  ) => void;
  
  type DialogContextValue = {
	openDialog: OpenDialog;
  };
  
  const DialogContext = createContext<DialogContextValue | null>(null);
  
  export const DialogProvider = ({ children }) => {
	const [open, setOpen] = useState(false);
	const [title, setTitle] = useState("");
	const [content, setContent] = useState("");
	const [closeCallback, setCloseCallback] = useState<(() => void) | null>(null);
	const [confirmCallback, setConfirmCallback] = useState<
	  (() => Promise<void> | void) | null
	>(null);
  
	const [loading, setLoading] = useState(false);
	const [total, setTotal] = useState<number | null>(null);
	const [done, setDone] = useState(0);
  
	const openDialog: OpenDialog = (title, content, confirmCb, closeCb) => {
	  setTitle(title);
	  setContent(content);
	  setConfirmCallback(() => confirmCb);
	  setCloseCallback(() => closeCb ?? null);
	  setLoading(false);
	  setTotal(null);
	  setDone(0);
	  setOpen(true);
	};
  
	const closeDialog = () => {
	  setOpen(false);
	  setLoading(false);
	  setTotal(null);
	  setDone(0);
	  closeCallback && closeCallback();
	};
  
	return (
	  <DialogContext.Provider value={{ openDialog }}>
		{children}
		<ConfirmDialog
		  open={open}
		  onClose={closeDialog}
		  onConfirm={confirmCallback}
		  title={title}
		  content={content}
		  loading={loading}
		  total={total}
		  done={done}
		  setLoading={setLoading}
		  setTotal={setTotal}
		  setDone={setDone}
		/>
	  </DialogContext.Provider>
	);
  };
  
export const useConfirmDialog = () => {
	return useContext(DialogContext);
};

type ConfirmDialogProps = {
	open: boolean;
	onClose: () => void;
	onConfirm: (() => Promise<void> | void) | null;
	title: string;
	content: string;
	loading: boolean;
	total: number | null;
	done: number;
	setLoading: (v: boolean) => void;
	setTotal: (v: number | null) => void;
	setDone: (v: number | ((prev: number) => number)) => void;
  };
  
  export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
	open,
	onClose,
	onConfirm,
	title,
	content,
	loading,
	total,
	done,
	setLoading,
	setTotal,
	setDone,
  }) => {
	const { t } = useTranslation(["common"]);
  
	const handleConfirmClick = async () => {
	  if (!onConfirm) return;
	  setLoading(true);
	  try {
		await onConfirm(); // dialog consumer controls progress updates
		onClose();
	  } finally {
		setLoading(false);
	  }
	};
  
	return (
	  <Dialog fullWidth maxWidth="sm" open={open} onClose={loading ? undefined : onClose}>
		<DialogTitle id="confirm-dialog-title">{title}</DialogTitle>
		<DialogContent>
		  <DialogContentText id="confirm-dialog-description">
			{content}
		  </DialogContentText>
		  {total != null && (
			<DialogContentText sx={{ mt: 1 }}>
			  {done}/{total}
			</DialogContentText>
		  )}
		</DialogContent>
		<DialogActions>
		  <Button color="inherit" onClick={onClose} disabled={loading}>
			{t("cancel")}
		  </Button>
		  <Button
			variant="contained"
			color="primary"
			onClick={handleConfirmClick}
			autoFocus
			disabled={loading}
		  >
			{loading ? t("loading") : t("confirm")}
		  </Button>
		</DialogActions>
	  </Dialog>
	);
  };
  